// types
import type { PlanGroup, PlanUnitMix } from 'components/types';

/**
 * A selector entry stands for a group of cost centers rather than a single one. The plan unit
 * selector marks those with a synthetic `<id>-group` uniqId, where a plain cost center keeps its
 * numeric id.
 */
const isCostCenterGroup = (unit: PlanUnitMix) =>
  typeof unit.uniqId === 'string' && unit.uniqId.includes('-group');

/**
 * The comma separated `costCenters` parameter the four roster queries take: the directly selected
 * cost centers first, then the cost centers of every selected group.
 *
 * Empty when nothing is selected, which is what the queries key their `enabled` guard on.
 *
 * `components/utils` has a shared `getPlanunitCommaIds` that looks like this one but is not a drop
 * in replacement, so do not fold the two together without deciding what should change: it keeps the
 * selection order where this one puts loose cost centers before group members (`5,6,7` against
 * `7,5,6` for the same mixed selection, so a different react-query key), it reads `id` where this
 * one reads `uniqId`, and it detects a group structurally on `costCenters` rather than on the
 * `-group` suffix. The sagas give every plan unit either its numeric id or a `-group` uniqId, so
 * the two agree on which units they keep. This function reproduces the pre-refactor inline code
 * verbatim; unifying them is a behaviour change, tracked in #2716.
 */
export const getCostCenterIdsFromPlanUnits = (planUnits: PlanUnitMix[]) => {
  const selectedCostCenterIds = planUnits
    .filter((unit) => typeof unit.uniqId === 'number')
    .map((unit) => unit.uniqId)
    .join(',');

  const groupedCostCenterIds = planUnits
    .filter(isCostCenterGroup)
    .flatMap((group) =>
      Array.isArray((group as PlanGroup).costCenters)
        ? (group as PlanGroup).costCenters.map((costCenter) => costCenter.id)
        : []
    )
    .join(',');

  return [selectedCostCenterIds, groupedCostCenterIds]
    .filter((ids) => ids)
    .join(',');
};
